import { useMemo } from 'react';
import { PAGE_TYPE } from '../types/pages.type';
import { getPercentageByTotal } from '../utilis/common';
import useAICalculationContext from './useAICalculationContext';
import useAICardDataContext from './useAICardDataContext';
import useAIFacilityContext from './useAIFacilityContext';
import useAIChartAndPatientContext from './useAIChartAndPatientContext';
import { generateChartAnalysis } from '../utilis/chart-analytics';

/**
 * Custom hook for AI Chat Context specific to OVERALL page
 * Handles Overall-specific filtering logic and card data
 */
export const useAIOverallChatContext = ({
  // Core data dependencies
  dbData,
  filterListData,
  patientList,
  activeFacilities,
  facilities,
  dynamicCards, // 🆕 ADD: Dynamic cards for notes filtering
  
  // Filter and state dependencies
  filter,
  transferType, // TOTAL, TOTAL_INCOMING, TOTAL_OUTGOING
  cardFilter,
  percentageBy,
  lockedTotalBy,
  specialComparisonSet,
  filterTotal,
  percentageAgainst,
  projectionDays,
  lastADTDate,
  
  // UI state dependencies - OVERALL SPECIFIC
  overallCards,
  rangesSet,
  isLayoutMode,
  
  // Function dependencies
  getPriorityNumber,
  lockedTotal,
  lockedByFacility,
  isCensusTotalLocked,
  facilityPercentage,
  
  // Metadata dependencies - OVERALL SPECIFIC
  OVERALL_CUSTOM_CARD_DATA,
  tooltipsInfo,
  
  // Service dependencies
  aiCensusActionsService,
  aiReportActionsService,
  aiTransferActionsService,
  aiDialogActionsService,
  aiDateSuggestionsService,
  
  // Chart data dependencies
  chartData = null,
  chartAggregationType = 'unknown',
  currentChartFilter = null,
  
  // Overall-specific dependencies
  shouldReverseOverallColors = false,
  comparingAgainstDifferentFacility = false
}) => {
  // Use sub-hooks for different aspects of the context
  const calculationContext = useAICalculationContext({
    filter,
    transferType,
    projectionDays,
    cardFilter,
    percentageBy,
    lockedTotalBy,
    specialComparisonSet,
    filterTotal,
    percentageAgainst
  });

  const cardDataContext = useAICardDataContext({
    pageType: PAGE_TYPE.OVERALL,
    filterListData,
    filter,
    transferType,
    projectionDays,
    cardFilter,
    percentageBy,
    lockedTotalBy,
    specialComparisonSet,
    filterTotal,
    percentageAgainst,
    dbData,
    getPriorityNumber,
    dynamicCards: dynamicCards || [], // 🆕 FIX: Use actual dynamicCards, not empty array
    patientList,
    OVERALL_CUSTOM_CARD_DATA
  });

  const facilityContext = useAIFacilityContext({
    activeFacilities,
    patientList,
    facilities,
    dbData,
    percentageBy,
    lockedTotal,
    lockedTotalBy,
    pageType: PAGE_TYPE.OVERALL
  });

  const chartAndPatientContext = useAIChartAndPatientContext();

  return useMemo(() => {
    // Calculate enhanced metrics for AI analysis (OVERALL PAGE SPECIFIC)
    const totalIncoming = dbData?.totalIncoming || 0; // Total incoming transfers
    const totalOutgoing = dbData?.totalOutgoing || 0; // Total outgoing transfers
    const netTransfer = totalIncoming - totalOutgoing; // Net transfer (+/-)
    
    // 🚨 CRITICAL: Current Transfer Type Filter
    // This determines which data is currently being displayed in ALL cards
    const currentTransferTypeFilter = transferType || 'total';
    const isShowingIncoming = currentTransferTypeFilter === 'totalIncoming';
    const isShowingOutgoing = currentTransferTypeFilter === 'totalOutgoing';
    const isShowingNet = currentTransferTypeFilter === 'total';
    
    // Get active filters count
    const activeFiltersCount = cardFilter ? Object.values(cardFilter).filter(arr => Array.isArray(arr) && arr.length > 0).length : 0;
    
    // Calculate data availability
    const dataCategories = filterListData ? Object.keys(filterListData).filter(key => 
      Array.isArray(filterListData[key]) && filterListData[key].length > 0
    ) : [];

    // Get the correct percentage base (same logic as cards use)
    // CRITICAL: Pass PAGE_TYPE.OVERALL to ensure average census is used as default baseline
    const currentPercentageBase = specialComparisonSet ? filterTotal : getPercentageByTotal(dbData, PAGE_TYPE.OVERALL);

    // OVERALL-SPECIFIC: Terminology definitions for AI
    const terminology = {
      totalIncoming: {
        label: "Total Incoming Transfers",
        meaning: "Patients admitted to the facility from external sources",
        description: "Patients transferred into the facility from hospitals, other SNFs, or admitted from the community. These are incoming transfers that increase the facility's census.",
        keyTerms: ["incoming", "incoming transfers", "admissions", "admitted from", "transferred in"]
      },
      totalOutgoing: {
        label: "Total Outgoing Transfers",
        meaning: "Patients discharged or transferred out from the facility",
        description: "Patients leaving the facility through discharges, transfers to hospitals, other facilities, or community. These are outgoing transfers that decrease the facility's census.",
        keyTerms: ["outgoing", "outgoing transfers", "discharges", "transferred out", "discharged to"]
      },
      netTransfer: {
        label: "Total +/-",
        meaning: "Net change in census (Incoming - Outgoing)",
        description: "The difference between incoming and outgoing transfers. A positive number means more patients came in than left (census increased), while a negative number means more patients left than came in (census decreased).",
        keyTerms: ["net transfer", "total", "+/-", "census change", "net change"]
      },
      important: "When users ask about 'transfers', clarify whether they mean incoming transfers, outgoing transfers, or net transfer (total +/-)."
    };

    // OVERALL-SPECIFIC: Percentage Calculation Context
    const percentageContext = {
      currentMethod: percentageBy,
      currentPercentageBase,
      percentageAgainst,
      filterTotal,
      specialComparisonSet,
      isComparingAgainstAvgCensus: filterListData?.isComparingAgainstAvgCensus,
      lockedTotalBy,
      transferType,
      avgCensus: dbData?.censusAverage,
      calculation: {
        description: `Percentages calculated using ${percentageBy} method`,
        baseValue: currentPercentageBase,
        explanation: specialComparisonSet 
          ? `Using filterTotal (${filterTotal}) as base because specialComparisonSet is true`
          : `Using percentageAgainst (${percentageAgainst}) as base from ${percentageBy} method`,
        overallSpecific: `Overall page compares against average census (${dbData?.censusAverage}) when locked`
      },
      // Priority Filter System Context (explains percentage base changes)
      priorityFiltering: {
        activePriorityFilters: cardFilter?.priorityData || [],
        totalActivePriorityFilters: cardFilter?.priorityData?.length || 0,
        firstPriorityCard: cardFilter?.priorityData?.[0]?.type || null,
        explanation: `When priority filters are applied on Overall page, percentage bases change:
          - First priority filter uses average census or locked total as base
          - Subsequent filters use filtered patient count as denominator
          - Example: If "Per Doctor" filter is applied first, percentages for other cards use filtered doctor transfers as base`,
        scalingLogic: `Percentages are scaled when:
          - isComparingAgainstAvgCensus is true
          - lockedTotalBy === "census"
          - First priority item AND no transfer type filter applied`,
      }
    };

    // Census Percentage Settings for AI interaction
    const censusSettings = (() => {
      try {
        const settings = aiCensusActionsService?.getCurrentPercentageSettings();
        return settings || null;
      } catch (error) {
        console.error('Error getting census settings:', error);
        return null;
      }
    })();

    // 🚨 CRITICAL: Filter information for AI context
    // This section explains what data is currently visible to the user
    const filterContext = {
      dateRange: {
        startDate: filter?.startDate,
        endDate: filter?.endDate,
        description: filter?.startDate && filter?.endDate 
          ? `From ${filter.startDate} to ${filter.endDate}`
          : 'No date range specified'
      },
      
      // 🚨 MOST IMPORTANT: Transfer Type Filter
      // This determines whether ALL card data shows incoming, outgoing, or net transfers
      transferTypeFilter: {
        current: currentTransferTypeFilter,
        isShowingIncoming,
        isShowingOutgoing,
        isShowingNet,
        options: ['total', 'totalIncoming', 'totalOutgoing'],
        
        // 🚨 CRITICAL EXPLANATION FOR AI
        currentViewExplanation: isShowingIncoming 
          ? '⚠️ CURRENTLY VIEWING: INCOMING TRANSFERS ONLY - All card data (doctorData, floorsData, insuranceData, etc.) shows ONLY incoming transfers. Do NOT include outgoing transfers in your responses.'
          : isShowingOutgoing
          ? '⚠️ CURRENTLY VIEWING: OUTGOING TRANSFERS ONLY - All card data (doctorData, floorsData, insuranceData, etc.) shows ONLY outgoing transfers. Do NOT include incoming transfers in your responses.'
          : isShowingNet
          ? '⚠️ CURRENTLY VIEWING: NET TRANSFERS (TOTAL +/-) - All card data shows the DIFFERENCE between incoming and outgoing (incoming - outgoing). A positive number means more incoming, negative means more outgoing.'
          : 'No transfer type filter applied (default: net transfers)',
        
        // Examples for AI understanding
        examples: {
          whenAskedAboutDoctor: isShowingIncoming
            ? 'If user asks "How many transfers for Dr. Smith?", respond with INCOMING transfers only from doctorData'
            : isShowingOutgoing
            ? 'If user asks "How many transfers for Dr. Smith?", respond with OUTGOING transfers only from doctorData'
            : 'If user asks "How many transfers for Dr. Smith?", respond with NET transfers (incoming - outgoing) from doctorData',
          
          whenAskedAboutFloor: isShowingIncoming
            ? 'If user asks "Show me Floor 2 transfers", respond with INCOMING transfers only for Floor 2'
            : isShowingOutgoing
            ? 'If user asks "Show me Floor 2 transfers", respond with OUTGOING transfers only for Floor 2'
            : 'If user asks "Show me Floor 2 transfers", respond with NET transfers (incoming - outgoing) for Floor 2'
        },
        
        // How to switch views (for AI to suggest)
        howToSwitch: {
          toIncoming: 'To view incoming transfers only, user should click the "Total Incoming Transfers" card',
          toOutgoing: 'To view outgoing transfers only, user should click the "Total Outgoing Transfers" card',
          toNet: 'To view net transfers (+/-), user should click the "Total +/-" card'
        }
      },
      
      activeCardFilters: activeFiltersCount,
      priorityFilters: cardFilter?.priorityData || []
    };

    // 🚨 CRITICAL: Overall-specific transfer details
    // This shows the TOTAL numbers regardless of current filter
    const transferDetails = {
      // INCOMING TRANSFERS (patients coming INTO the facility)
      incoming: {
        total: totalIncoming,
        breakdown: dbData?.incomingDetailsTotal || null, // Detailed breakdown of incoming sources
        percentage: percentageAgainst ? ((totalIncoming / percentageAgainst) * 100).toFixed(1) : 0,
        description: 'Patients admitted to the facility from hospitals, other SNFs, or community',
        sources: dbData?.incomingDetailsTotal ? {
          // Parse incoming details if available
          raw: dbData.incomingDetailsTotal,
          explanation: 'Breakdown by admission source (hospital, SNF, community, etc.)'
        } : null
      },
      
      // OUTGOING TRANSFERS (patients leaving the facility)
      outgoing: {
        total: totalOutgoing,
        breakdown: dbData?.outGoingDetailsTotal || null, // Detailed breakdown of outgoing destinations
        percentage: percentageAgainst ? ((totalOutgoing / percentageAgainst) * 100).toFixed(1) : 0,
        description: 'Patients discharged or transferred out to hospitals, other facilities, or community',
        destinations: dbData?.outGoingDetailsTotal ? {
          // Parse outgoing details if available
          raw: dbData.outGoingDetailsTotal,
          explanation: 'Breakdown by discharge destination (hospital, home, hospice, etc.)'
        } : null
      },
      
      // NET TRANSFER (incoming - outgoing)
      net: {
        value: netTransfer,
        direction: netTransfer > 0 ? 'positive (more incoming than outgoing)' : netTransfer < 0 ? 'negative (more outgoing than incoming)' : 'balanced (equal incoming and outgoing)',
        percentage: percentageAgainst ? ((netTransfer / percentageAgainst) * 100).toFixed(1) : 0,
        description: 'The difference between incoming and outgoing transfers',
        impact: netTransfer > 0 
          ? `Census INCREASED by ${Math.abs(netTransfer)} patients` 
          : netTransfer < 0 
          ? `Census DECREASED by ${Math.abs(netTransfer)} patients`
          : 'Census remained STABLE (no net change)'
      },
      
      // 🚨 CRITICAL: What data is user currently viewing?
      currentlyDisplayingInCards: isShowingIncoming 
        ? {
            type: 'INCOMING ONLY',
            explanation: 'All cards (Per Doctor, Per Floor, Per Insurance, etc.) currently show INCOMING transfers only',
            totalShown: totalIncoming,
            excludes: 'Outgoing transfers are NOT included in any card data'
          }
        : isShowingOutgoing
        ? {
            type: 'OUTGOING ONLY',
            explanation: 'All cards (Per Doctor, Per Floor, Per Insurance, etc.) currently show OUTGOING transfers only',
            totalShown: totalOutgoing,
            excludes: 'Incoming transfers are NOT included in any card data'
          }
        : {
            type: 'NET TRANSFERS (TOTAL +/-)',
            explanation: 'All cards show the NET difference (incoming - outgoing) for each category',
            totalShown: netTransfer,
            calculation: `${totalIncoming} incoming - ${totalOutgoing} outgoing = ${netTransfer} net`
          }
    };

    // 🚨 CRITICAL: Card-specific information
    // Each card's data changes based on the current transfer type filter
    const currentDataType = isShowingIncoming ? 'INCOMING transfers' : isShowingOutgoing ? 'OUTGOING transfers' : 'NET transfers';
    
    const cardInformation = {
      doctorData: {
        label: 'Per Doctor',
        description: `Transfer patterns by attending physician (showing ${currentDataType})`,
        available: filterListData?.doctorData?.length > 0,
        count: filterListData?.doctorData?.length || 0,
        currentlyShowing: currentDataType,
        dataSource: filterListData?.doctorData || []
      },
      floorsData: {
        label: 'Per Floor/Unit',
        description: `Transfer patterns by floor or unit (showing ${currentDataType})`,
        available: filterListData?.floorsData?.length > 0,
        count: filterListData?.floorsData?.length || 0,
        currentlyShowing: currentDataType,
        dataSource: filterListData?.floorsData || []
      },
      insuranceData: {
        label: 'Per Insurance',
        description: `Transfer patterns by insurance payer (showing ${currentDataType})`,
        available: filterListData?.insuranceData?.length > 0,
        count: filterListData?.insuranceData?.length || 0,
        currentlyShowing: currentDataType,
        dataSource: filterListData?.insuranceData || []
      },
      daysData: {
        label: 'Per Day of Week',
        description: `Transfer patterns by day of the week (showing ${currentDataType})`,
        available: filterListData?.daysData?.length > 0,
        count: filterListData?.daysData?.length || 0,
        currentlyShowing: currentDataType,
        dataSource: filterListData?.daysData || []
      },
      ninetyDaysData: {
        label: '90 Days Analysis',
        description: `Analysis of transfers over 90-day periods (showing ${currentDataType})`,
        available: filterListData?.ninetyDaysData?.length > 0,
        count: filterListData?.ninetyDaysData?.length || 0,
        currentlyShowing: currentDataType,
        dataSource: filterListData?.ninetyDaysData || [],
        note: '⚠️ Only available when viewing OUTGOING transfers (not available for incoming or net)'
      }
    };

    // Available actions through AI
    const availableActions = {
      census: censusSettings?.availableActions || [],
      reporting: aiReportActionsService ? ['generatePDFReport', 'generateExcelReport', 'scheduleAutomatedReport'] : [],
      transfers: aiTransferActionsService ? ['analyzeTransferPatterns', 'compareTransfers'] : [],
      dialog: aiDialogActionsService ? ['openChartDialog', 'openDetailsDialog'] : [],
      dateManipulation: aiDateSuggestionsService ? ['suggestDateRange', 'comparePeriods'] : []
    };

    // Chart data analysis (if available)
    const chartAnalysis = chartData ? generateChartAnalysis(
      chartData,
      chartAggregationType,
      currentChartFilter,
      PAGE_TYPE.OVERALL
    ) : null;

    // Overall page state information
    const pageState = {
      isLoading: false,
      isLayoutMode,
      rangesSet,
      hasData: patientList?.list?.length > 0,
      dataCount: patientList?.list?.length || 0,
      shouldReverseOverallColors,
      comparingAgainstDifferentFacility
    };

    // Build comprehensive context object
    return {
      // 🚨🚨🚨 READ THIS FIRST - CRITICAL FOR CORRECT AI RESPONSES 🚨🚨🚨
      CRITICAL_FIRST_READ: {
        currentTransferView: currentTransferTypeFilter,
        isShowingIncoming,
        isShowingOutgoing,
        isShowingNet,
        
        URGENT_MESSAGE: isShowingIncoming
          ? '🔴 USER IS VIEWING INCOMING TRANSFERS ONLY - All card data shows ONLY incoming transfers. Do NOT include outgoing data in your responses unless specifically comparing totals.'
          : isShowingOutgoing
          ? '🔴 USER IS VIEWING OUTGOING TRANSFERS ONLY - All card data shows ONLY outgoing transfers. Do NOT include incoming data in your responses unless specifically comparing totals.'
          : '🔴 USER IS VIEWING NET TRANSFERS - All card data shows the DIFFERENCE (incoming - outgoing). Positive means more incoming, negative means more outgoing.',
        
        WHAT_DATA_IS_VISIBLE: {
          doctorData: `Shows ${currentDataType} per doctor`,
          floorsData: `Shows ${currentDataType} per floor`,
          insuranceData: `Shows ${currentDataType} per insurance`,
          daysData: `Shows ${currentDataType} per day`,
          ninetyDaysData: isShowingOutgoing ? `Shows ${currentDataType} over 90-day periods` : 'NOT AVAILABLE (only for outgoing transfers)'
        },
        
        WHEN_USER_ASKS_ABOUT_COUNTS: `Report the values from the current view (${currentDataType}). Example: If user asks "How many transfers for Dr. Smith?", look in cardData.doctorData and report the value shown for Dr. Smith, which represents ${currentDataType}.`,
        
        TOTAL_NUMBERS_FOR_REFERENCE: {
          totalIncoming,
          totalOutgoing,
          netTransfer,
          note: 'These totals are always available, but card data reflects the current view filter'
        }
      },
      
      // Page identification
      pageType: PAGE_TYPE.OVERALL,
      pageName: 'Overall Dashboard',
      
      // Core metrics
      metrics: {
        totalIncoming,
        totalOutgoing,
        netTransfer,
        avgCensus: dbData?.censusAverage || 0,
        projectionDays,
        lastADTDate
      },
      
      // Transfer details
      transferDetails,
      
      // Terminology
      terminology,
      
      // Calculation contexts from sub-hooks
      ...calculationContext,
      ...cardDataContext,
      ...facilityContext,
      ...chartAndPatientContext,
      
      // Overall-specific contexts
      percentageContext,
      filterContext,
      cardInformation,
      availableActions,
      chartAnalysis,
      pageState,
      
      // Data availability
      dataAvailability: {
        hasData: patientList?.list?.length > 0,
        dataCategories,
        categoriesCount: dataCategories.length,
        activeFilters: activeFiltersCount,
        customTabs: patientList?.customTabs?.length || 0
      },
      
      // Locked states
      lockStates: {
        lockedTotal,
        lockedByFacility,
        isCensusTotalLocked,
        lockedTotalBy,
        facilityPercentage
      },
      
      // Feature flags
      features: {
        comparisonMode: rangesSet,
        layoutEditMode: isLayoutMode,
        reversedColors: shouldReverseOverallColors,
        comparingDifferentFacility: comparingAgainstDifferentFacility
      },
      
      // CRITICAL: Custom/Dynamic Cards (for notes filtering)
      customCards: {
        dynamicCards: dynamicCards || [],
        customTabs: patientList?.customTabs || []
      },
      
      // 🚨 CRITICAL: Help text for AI understanding
      helpText: {
        overallPagePurpose: 'The Overall page provides a comprehensive view of all patient transfers (incoming and outgoing) across the facility, allowing analysis of transfer patterns by various dimensions like doctor, floor, insurance, and time.',
        
        // 🚨 MOST IMPORTANT SECTION FOR AI
        criticalIncomingOutgoingLogic: {
          summary: '⚠️ THE MOST CRITICAL THING TO UNDERSTAND: The current transfer type filter determines what data is shown in ALL cards',
          
          currentView: isShowingIncoming 
            ? '🔴 USER IS CURRENTLY VIEWING: INCOMING TRANSFERS ONLY'
            : isShowingOutgoing
            ? '🔴 USER IS CURRENTLY VIEWING: OUTGOING TRANSFERS ONLY'
            : '🔴 USER IS CURRENTLY VIEWING: NET TRANSFERS (TOTAL +/-)',
          
          whatThisMeans: isShowingIncoming
            ? `All numbers in all cards represent INCOMING transfers ONLY. If user asks "how many transfers does Dr. Smith have?", you should respond with the INCOMING transfer count for Dr. Smith from doctorData. DO NOT mention or include outgoing transfers unless specifically asked about total incoming vs outgoing comparison.`
            : isShowingOutgoing
            ? `All numbers in all cards represent OUTGOING transfers ONLY. If user asks "how many transfers does Dr. Smith have?", you should respond with the OUTGOING transfer count for Dr. Smith from doctorData. DO NOT mention or include incoming transfers unless specifically asked about total incoming vs outgoing comparison.`
            : `All numbers in all cards represent NET transfers (incoming minus outgoing). If user asks "how many transfers does Dr. Smith have?", you should respond with the NET transfer count for Dr. Smith from doctorData, explaining that this is the difference between incoming and outgoing.`,
          
          exampleResponses: {
            userAsks: 'How many transfers does Dr. Smith have?',
            correctAnswer: isShowingIncoming
              ? 'Find Dr. Smith in doctorData and report the INCOMING transfer count. Example: "Dr. Smith has 15 incoming transfers."'
              : isShowingOutgoing
              ? 'Find Dr. Smith in doctorData and report the OUTGOING transfer count. Example: "Dr. Smith has 12 outgoing transfers."'
              : 'Find Dr. Smith in doctorData and report the NET transfer count. Example: "Dr. Smith has a net of +3 transfers (meaning 3 more patients came in than went out under their care)."',
            wrongAnswer: 'Reporting incoming when user is viewing outgoing, or vice versa. Always check transferDetails.currentlyDisplayingInCards to know what data is shown.'
          },
          
          howToKnowWhatToShow: [
            '1. Check transferDetails.currentlyDisplayingInCards.type to see what view is active',
            '2. Check filterContext.transferTypeFilter.currentViewExplanation for detailed explanation',
            '3. All card data (doctorData, floorsData, insuranceData, etc.) will reflect this filter',
            '4. The filterListData contains the filtered data - just use it directly based on the current view'
          ],
          
          totalNumbers: {
            alwaysAvailable: `Total Incoming: ${totalIncoming}, Total Outgoing: ${totalOutgoing}, Net: ${netTransfer}`,
            explanation: 'These totals are always available regardless of current view. You can mention them for context, but when analyzing cards, use the data that matches the current view.'
          }
        },
        
        transferTypeExplanation: 'Users can view net transfers (Total +/-), only incoming transfers, or only outgoing transfers by selecting the respective cards at the top of the page.',
        filteringLogic: 'Filters work in priority order - the first filter applied becomes the baseline for subsequent filters.',
        percentageCalculation: 'Percentages are typically calculated against average census but can be locked to specific totals for comparison purposes.',
        customCardsInfo: overallCards?.length > 0 ? `${overallCards.length} custom cards are currently selected for display` : 'No custom cards selected',
        tooltipInfo: tooltipsInfo?.overallPage || null,
        
        // Common user questions and how to handle them
        commonScenarios: {
          scenario1: {
            question: 'User asks "Which doctor has the most transfers?"',
            response: `Look at doctorData and find the doctor with the highest value. Make sure to mention you're looking at ${currentDataType}.`
          },
          scenario2: {
            question: 'User asks about both incoming and outgoing',
            response: 'You have access to transferDetails.incoming.total and transferDetails.outgoing.total. Use both and explain the difference.'
          },
          scenario3: {
            question: 'User asks about a specific floor/doctor/insurance but current view is different',
            response: `Report the data from the current view (${currentDataType}) but mention that they can switch views if needed using the Total Incoming/Outgoing cards.`
          }
        }
      },
      
      // Raw data for advanced queries
      rawData: {
        dbData,
        filterListData,
        patientList,
        cardFilter,
        filter
      }
    };
  }, [
    // Core data dependencies
    dbData, filterListData, patientList, dynamicCards, // 🆕 ADD: dynamicCards
    // Filter dependencies
    filter, transferType, cardFilter, percentageBy, lockedTotalBy,
    specialComparisonSet, filterTotal, percentageAgainst, projectionDays, lastADTDate,
    // UI state
    overallCards, rangesSet, isLayoutMode,
    // Functions and states
    lockedTotal, lockedByFacility, isCensusTotalLocked,
    facilityPercentage, tooltipsInfo,
    // Services
    aiCensusActionsService, aiReportActionsService, aiTransferActionsService,
    aiDialogActionsService, aiDateSuggestionsService,
    // Chart data
    chartData, chartAggregationType, currentChartFilter,
    // Overall specific
    shouldReverseOverallColors, comparingAgainstDifferentFacility,
    // Sub-hook contexts (already memoized)
    calculationContext, cardDataContext, facilityContext, chartAndPatientContext
  ]);
};

export default useAIOverallChatContext;

